<template>
  <div class="w-full border rounded overflow-hidden bg-gray-50 dark:bg-gray-900">
    <div class="w-full px-3 py-2 flex items-center justify-between">
      <div>
        <div v-if="loading" class="flex items-center">
          <o-loader
            :scale="0.3"
            class="h-8 w-8 mr-4"
          />
          <span class="align-middle text-sm leading-none text-gray-800 dark:text-gray-100">{{ $t('customer.loading') }} {{ syncProgress ? `(${Math.round(syncProgress * 100)}%)` : '' }} ...</span>
        </div>
        <h4 v-else class="font-semibold text-sm leading-none text-gray-800 dark:text-gray-100">
          <span>{{ $t('customer.customersSelected', { count: _value.length }, _value.length) }}</span>
        </h4>
      </div>

      <div class="flex-grow flex items-center justify-end space-x-2">
        <o-filters :filters="filters" />
      </div>
    </div>

    <div class="border-t">
      <div class="h-full bg-white overflow-hidden flex flex-col dark:bg-gray-900">
        <div class="flex items-center justify-between px-3 py-2 border-b bg-gray-50 dark:bg-gray-900">
          <p class="text-sm text-gray-600 dark:text-gray-300 font-medium leading-none">
            {{ $t('general.customer') }}
          </p>

          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600 dark:text-gray-300">{{ $t('general.selectAll') }}</label>
            <input
              ref="select-all"
              type="checkbox"
              :checked="filtered.length === _value.length"
              class="o-checkbox appearance-none h-4 w-4 border border-gray-300 rounded  checked:bg-blue-600 checked:border-blue-600"
              @click="handleSelectAll"
            >
          </div>
        </div>
        <o-table
          :data="filtered"
          :limit="10"
          :filter-keys="[
            'firstName',
            'lastName'
          ]"
          scroll
          container-class="overflow-hidden"
          class="h-full"
        >
          <template #row="{ row }">
            <checkbox-field
              v-model="_value"
              :value="row"
              :label="row.name"
              option-class="py-1 px-3 flex flex-row-reverse justify-between items-center rounded transition duration-200"
            >
              <div class="flex items-center justify-between w-full">
                <div class="flex items-center space-x-2">
                  <o-avatar :items="[row]" />

                  <div class="text-sm">
                    <p class="text-gray-800 dark:text-gray-100">
                      {{ row.displayName }}
                    </p>
                    <p class="text-gray-600 dark:text-gray-300">
                      {{ row.email }} <span v-if="row.email && row.phone">-</span> {{ row.phone }}
                    </p>
                  </div>
                </div>
              </div>
            </checkbox-field>
          </template>
        </o-table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import { useCustomerFilters } from '~/composables/filters/useCustomerFilters'

import {
  OAvatar,
  OTable,
  OFilters,
} from '@/components/elements'

import {
  CheckboxField,
} from '@/components/fields'

import { useCustomerStore } from '~/stores/useCustomerStore'
import { useFormStore } from '~/stores/useFormStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'
import { useServiceStore } from '~/stores/useServiceStore'
import { useSegmentStore } from '~/stores/useSegmentStore'
import { useProductStore } from '~/stores/useProductStore'

interface Props {
  modelValue?: any[]
  filterPreset?: Record<string, any> | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  filterPreset: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const customerStore = useCustomerStore()
const formStore = useFormStore()
const employeeStore = useEmployeeStore()
const serviceStore = useServiceStore()
const segmentStore = useSegmentStore()
const productStore = useProductStore()

const filteredResults = ref<any[]>([])
const filters = reactive(useCustomerFilters())
const isFiltering = ref(false)
const onlySelected = ref(false)

const isSyncing = computed(() => customerStore.isSyncing)
const syncProgress = computed(() => customerStore.syncProgress)
const syncDate = computed(() => customerStore.syncDate)
const all = computed(() => customerStore.all)
const forms = computed(() => formStore.all)
const employees = computed(() => employeeStore.all)
const services = computed(() => serviceStore.categorised)
const segments = computed(() => segmentStore.all)
const products = computed(() => productStore.all)

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
  },
})

const filtered = computed(() => {
  let items: any[]

  if (filters.isEmpty) {
    items = all.value
  } else {
    items = filteredResults.value
  }

  if (onlySelected.value) {
    return items
      .filter((item: any) => {
        return _value.value.includes(item?.idCustomer)
      })
  }

  return items
})

const filterValues = computed(() => {
  const values: Record<string, any> = {}
  for (const [key, state] of Object.entries(filters.filterStates)) {
    values[key] = state.value
  }
  return values
})

const filteredTotal = computed(() => {
  return filtered.value.length
})

const loading = computed(() => {
  return isSyncing.value || isFiltering.value
})

const doFilter = debounce(async () => {
  isFiltering.value = true
  filteredResults.value = await filters.filter(all.value)
}, 500)

watch(filterValues, () => {
  doFilter()
}, { deep: true, immediate: true })

watch(filteredResults, () => {
  isFiltering.value = false
})

watch(forms, () => {
  filters.updateFormOptions()
})

watch(employees, () => {
  filters.updateEmployeeOptions()
})

watch(services, () => {
  filters.updateServiceOptions()
})

watch(segments, () => {
  filters.updateSegmentOptions()
})

watch(products, () => {
  filters.updateProductOptions()
})

onMounted(() => {
  customerStore.sync()
  serviceStore.sync()
  employeeStore.sync()
  formStore.sync()
  segmentStore.sync()
  productStore.sync()

  if (props.filterPreset) {
    filters.fromJSON(props.filterPreset)
  }
})

const handleSelectAll = () => {
  if (filtered.value.length === _value.value.length) {
    _value.value = []
  } else {
    _value.value = filtered.value
  }
}
</script>
